import { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { NotificationProvider } from './contexts/NotificationContext'
import { SocketProvider } from './contexts/SocketContext'

// Import components
import Login from './components/auth/Login'
import Register from './components/auth/Register'
import ForgotPassword from './components/auth/ForgotPassword'
import ResetPassword from './components/auth/ResetPassword'
import WhatsAppSignIn from './components/auth/WhatsAppSignIn'
import CustomerHome from './components/customer/CustomerHome'
import ScheduledWorks from './components/customer/ScheduledWorks'
import AssetTracker from './components/customer/AssetTracker'
import ServiceRequest from './components/customer/ServiceRequest'
import Chat from './components/customer/Chat'
import OrderTracking from './components/customer/OrderTracking'
import AddressBook from './components/customer/AddressBook'
import Profile from './components/customer/Profile'
import AIInspect from './components/customer/AIInspect'
import ProfessionalProfile from './components/customer/ProfessionalProfile'
import BookingDateTime from './components/customer/BookingDateTime'
import BookingReview from './components/customer/BookingReview'
import BookingConfirmation from './components/customer/BookingConfirmation'
import PaymentMethods from './components/customer/PaymentMethods'
import JobDetails from './components/customer/JobDetails'
import ProfessionalsList from './components/customer/ProfessionalsList'
import ServiceCards from './components/customer/ServiceCards'
import BottomNav from './components/common/BottomNav'
import LoadingSpinner from './components/common/LoadingSpinner'
import ErrorBoundary from './components/common/ErrorBoundary'
import NetworkStatus from './components/common/NetworkStatus'
import Toast from './components/common/Toast'

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()
  
  if (loading) {
    return <LoadingSpinner fullScreen />
  }
  
  if (!user) {
    return <Navigate to="/login" replace />
  }
  
  return children
}

// Screens of the sign-in flow (reachable without a session)
const AUTH_PATHS = ['/login', '/register', '/forgot-password', '/reset-password', '/login/whatsapp']

// Main App Layout
function AppLayout() {
  const [activeTab, setActiveTab] = useState('home')
  const { user } = useAuth()
  const location = useLocation()
  
  // Hide bottom nav on focused/detail pages (industry best practice)
  const hideBottomNav = 
    location.pathname === '/ai-inspect' ||           // AI Inspector (full-screen experience)
    location.pathname === '/chat' ||                 // Chat (full-screen conversation)
    location.pathname.startsWith('/professionals/') || // Professional Profile (has own actions)
    location.pathname.startsWith('/booking/') ||     // Booking Flow (focused task)
    location.pathname.startsWith('/jobs/') ||        // Job Details (active job monitoring)
    AUTH_PATHS.includes(location.pathname)           // sign-in screens (e.g. "Set a password" right after a WhatsApp code)

  return (
    <div className="mobile-container bg-white">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/login/whatsapp" element={<WhatsAppSignIn />} />
        <Route path="/" element={
          <ProtectedRoute>
            <CustomerHome />
          </ProtectedRoute>
        } />
        <Route path="/scheduled" element={
          <ProtectedRoute>
            <ScheduledWorks />
          </ProtectedRoute>
        } />
        <Route path="/assets" element={
          <ProtectedRoute>
            <AssetTracker />
          </ProtectedRoute>
        } />
        <Route path="/request" element={
          <ProtectedRoute>
            <ServiceRequest />
          </ProtectedRoute>
        } />
        <Route path="/chat" element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        } />
        <Route path="/ai-inspect" element={
          <ProtectedRoute>
            <AIInspect />
          </ProtectedRoute>
        } />
        <Route path="/orders" element={
          <ProtectedRoute>
            <OrderTracking />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } />
        <Route path="/addresses" element={
          <ProtectedRoute>
            <AddressBook />
          </ProtectedRoute>
        } />
        <Route path="/professionals/:id" element={
          <ProtectedRoute>
            <ProfessionalProfile />
          </ProtectedRoute>
        } />
        <Route path="/booking/datetime/:professionalId" element={
          <ProtectedRoute>
            <BookingDateTime />
          </ProtectedRoute>
        } />
        <Route path="/booking/review/:professionalId" element={
          <ProtectedRoute>
            <BookingReview />
          </ProtectedRoute>
        } />
        <Route path="/booking/confirmation" element={
          <ProtectedRoute>
            <BookingConfirmation />
          </ProtectedRoute>
        } />
        <Route path="/payment-methods" element={
          <ProtectedRoute>
            <PaymentMethods />
          </ProtectedRoute>
        } />
        <Route path="/jobs/:id" element={
          <ProtectedRoute>
            <JobDetails />
          </ProtectedRoute>
        } />
        <Route path="/professionals" element={
          <ProtectedRoute>
            <ProfessionalsList />
          </ProtectedRoute>
        } />
        <Route path="/services/:serviceType" element={
          <ProtectedRoute>
            <ServiceCards />
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {user && !hideBottomNav && <BottomNav active={activeTab} onNav={setActiveTab} />}
    </div>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <NotificationProvider>
          <SocketProvider>
            <Router>
              <NetworkStatus />
              <Toast />
              <AppLayout />
            </Router>
          </SocketProvider>
        </NotificationProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
